/**
 * 行政区划管理前端接口-支持全部国家；
 * @author ChaseZhang
 * @since 2025-07-25
 */

import { httpClient as request } from '@/utils/http'

const JAVA_LONG_MAX_VALUE = '9223372036854775807'

/**
 * 校验并规范化后端 Long 类型的行政区划 ID，避免把自定义文本发给级联查询接口。
 */
function normalizeDivisionParentId(value: string): string | undefined {
  const text = String(value ?? '').trim()
  if (!/^\d+$/.test(text)) return undefined
  const normalized = text.replace(/^0+(?=\d)/, '')
  if (
    normalized.length > JAVA_LONG_MAX_VALUE.length ||
    (normalized.length === JAVA_LONG_MAX_VALUE.length && normalized > JAVA_LONG_MAX_VALUE)
  ) {
    return undefined
  }
  return text
}

// ==================== 类型定义 ====================

/**
 * 行政区划层级枚举
 */
export enum DivisionLevel {
  /** 一级行政区划（省/州） - level 2 */
  FIRST_LEVEL = 2,
  /** 二级行政区划（市/县） - level 3 */
  SECOND_LEVEL = 3,
  /** 三级行政区划（区/县） - level 4 */
  THIRD_LEVEL = 4,
  /** 四级行政区划（街道/乡镇） - level 5 */
  FOURTH_LEVEL = 5,
  /** 五级行政区划（村/居委会） - level 6 */
  FIFTH_LEVEL = 6
}

/**
 * 启用状态枚举
 */
export enum EnabledStatus {
  /** 禁用 */
  DISABLED = 0,
  /** 启用 */
  ENABLED = 1
}

/**
 * 中国行政区划VO模型
 */
export interface ChinaAdministrativeDivisionVO {
  /** 唯一标识 */
  id: string;
  /** 租户ID */
  tenantId?: string;
  /** 国家ID */
  countryId?: string;
  /** 国家名称 */
  countryName?: string;
  /** 国家英文名称 */
  countryNameEn?: string;
  /** 上级行政区划ID */
  parentId: string;
  /** 上级行政区划名称 */
  parentName?: string;
  /** 行政区划中文名称 */
  nameCn: string;
  /** 行政区划英文名称 */
  nameEn?: string;
  /** 邮政编码 */
  postalCode?: string;
  /** 行政区划编码 */
  code?: string;
  /** 一级行政区类型编码 */
  category?: number;
  /** 一级行政区类型名称 */
  categoryName?: string;
  /** 层级：1-国家, 2-一级行政区划, 3-二级行政区划, 4-三级行政区划, 5-四级行政区划, 6-五级行政区划 */
  level: number;
  /** 层级名称 */
  levelName?: string;
  /** 启用状态：1-启用, 0-禁用 */
  enabled: EnabledStatus;
  /** 是否为平台数据 */
  isPlatformData?: boolean;
  /** 是否有子级 */
  hasChildren?: boolean;
  /** 子级数量 */
  childrenCount?: number;
  /** 完整路径 */
  fullPath?: string;
  /** 完整英文路径 */
  fullPathEn?: string;
  /** 创建时间 */
  dateCreated?: string;
  /** 更新时间 */
  dateModified?: string;

  // 兼容原有字段名
  /** @deprecated 使用 nameCn 替代 */
  name?: string;
}

/**
 * 中国行政区划查询请求参数
 */
export interface ChinaAdministrativeDivisionQueryRequest {
  /** 当前页码，默认为1 */
  pageNum?: number;
  /** 每页大小，默认为10 */
  pageSize?: number;
  /** 租户ID */
  tenantId?: string;
  /** 关键词搜索 */
  keyword?: string;
  /** 上级行政区划ID */
  parentId?: string;
  /** 行政区划名称，支持模糊查询 */
  name?: string;
  /** 行政区划编码 */
  code?: string;
  /** 一级行政区类型编码 */
  category?: number;
  /** 层级：2-一级行政区划, 3-二级行政区划, 4-三级行政区划, 5-四级行政区划, 6-五级行政区划 */
  level?: DivisionLevel;
  /** 启用状态：1-启用, 0-禁用 */
  enabled?: EnabledStatus;
  /** 国家筛选 */
  country?: string;
  /** 国家ID */
  countryId?: string;
}

/**
 * 中国行政区划保存请求参数
 */
export interface ChinaAdministrativeDivisionSaveRequest {
  /** 主键ID，更新时传递 */
  id?: string;
  /** 租户ID */
  tenantId?: string;
  /** 国家ID */
  countryId?: string;
  /** 上级行政区划ID，顶级为"0" */
  parentId: string;
  /** 行政区划中文名称 */
  nameCn: string;
  /** 行政区划英文名称 */
  nameEn?: string;
  /** 行政区划编码，国家标准编码 */
  code?: string;
  /** 一级行政区类型编码 */
  category?: number;
  /** 邮政编码 */
  postalCode?: string;
  /** 层级：1-国家, 2-一级行政区划, 3-二级行政区划, 4-三级行政区划, 5-四级行政区划, 6-五级行政区划 */
  level: number;
  /** 启用状态：1-启用, 0-禁用，默认为1 */
  enabled?: EnabledStatus;

  // 兼容原有字段名
  /** @deprecated 使用 nameCn 替代 */
  name?: string;
}

/**
 * 模糊搜索请求参数
 */
export interface FuzzySearchRequest {
  /** 搜索关键字 */
  keyword?: string;
  /** 当前页码，默认为1 */
  pageNum?: number;
  /** 每页大小，默认为10 */
  pageSize?: number;
  /** 租户ID */
  tenantId?: string;
  /** 启用状态 */
  enabled?: EnabledStatus;
  /** 层级 */
  level?: DivisionLevel;
  /** 上级行政区划ID */
  parentId?: string;
  /** 国家ID */
  countryId?: string;
  /** 一级行政区类型编码 */
  category?: number;
}

export interface CityFuzzySearchRequest {
  /** 国家ID；为空时在全部国家范围内查询 */
  countryId?: string;
  /** 上级省州ID；为空时按国家或全部国家查询城市 */
  parentId?: string;
  /** 中文名、英文名或地区代码 */
  keyword?: string;
}

export interface ProvinceFuzzySearchRequest {
  /** 国家ID；为空时在全部国家范围内查询 */
  countryId?: string;
  /** 中文名、英文名或地区代码 */
  keyword?: string;
}

/**
 * 分页响应结果
 */
export interface PageResultVO<T> {
  /** 当前页码 */
  pageNum: number;
  /** 每页大小 */
  pageSize: number;
  /** 总记录数 */
  total: number;
  /** 数据列表 */
  records: T[];
}

// ==================== API 类 ====================

/**
 * 中国行政区划管理API
 */
export class ChinaAdministrativeDivisionApi {
  private static readonly BASE_URL = '/mdm/administrativeDivision'

  // ==================== 核心CRUD接口 ====================

  /**
   * 分页查询中国行政区划列表
   */
  static queryPage = async(params: ChinaAdministrativeDivisionQueryRequest): Promise<PageResultVO<ChinaAdministrativeDivisionVO>> => {
    return request.post(`${this.BASE_URL}/queryPage`, params)
  }

  /**
   * 查询中国行政区划列表
   */
  static queryList = async(params: ChinaAdministrativeDivisionQueryRequest): Promise<ChinaAdministrativeDivisionVO[]> => {
    return request.post(`${this.BASE_URL}/queryList`, params)
  }

  /**
   * 模糊搜索中国行政区划
   */
  static search = async(params: FuzzySearchRequest): Promise<ChinaAdministrativeDivisionVO[]> => {
    return request.post(`${this.BASE_URL}/search`, params)
  }

  /**
   * 远程搜索城市，可选国家和省州范围，未输入关键词时默认返回前10条。
   */
  static searchCities = async(params: CityFuzzySearchRequest): Promise<ChinaAdministrativeDivisionVO[]> => {
    const { parentId, ...searchParams } = params
    const normalizedParentId = parentId ? normalizeDivisionParentId(parentId) : undefined
    return this.search({
      ...searchParams,
      ...(normalizedParentId ? { parentId: normalizedParentId } : {}),
      pageNum: 1,
      pageSize: 10,
      level: DivisionLevel.SECOND_LEVEL,
      enabled: EnabledStatus.ENABLED
    })
  }

  /**
   * 远程搜索省州，可选国家范围，未输入关键词时默认返回前10条。
   */
  static searchProvinces = async(params: ProvinceFuzzySearchRequest): Promise<ChinaAdministrativeDivisionVO[]> => {
    return this.search({
      ...params,
      pageNum: 1,
      pageSize: 10,
      level: DivisionLevel.FIRST_LEVEL,
      enabled: EnabledStatus.ENABLED
    })
  }

  /**
   * 根据ID获取中国行政区划详情
   */
  static getById = async(id: string): Promise<ChinaAdministrativeDivisionVO> => {
    return request.post(`${this.BASE_URL}/getById`, { id })
  }

  /**
   * 保存中国行政区划（新增或更新）
   */
  static save = async(params: ChinaAdministrativeDivisionSaveRequest): Promise<boolean> => {
    return request.post(`${this.BASE_URL}/save`, params)
  }

  /**
   * 删除中国行政区划
   */
  static delete = async(ids: string[]): Promise<boolean> => {
    return request.post(`${this.BASE_URL}/delete`, { ids })
  }

  // ==================== 批量操作接口 ====================

  /**
   * 批量删除中国行政区划
   */
  static batchDelete = async(ids: string[]): Promise<boolean> => {
    return request.post(`${this.BASE_URL}/delete`, { ids })
  }

  /**
   * 更新启用状态
   */
  static updateStatus = async(ids: string[], enabled: EnabledStatus): Promise<boolean> => {
    return request.post(`${this.BASE_URL}/updateStatus`, { ids, enabled })
  }

  /**
   * 批量更新启用状态
   */
  static batchUpdateStatus = async(ids: string[], enabled: EnabledStatus): Promise<boolean> => {
    return request.post(`${this.BASE_URL}/updateStatus`, { ids, enabled })
  }

  // ==================== 专项查询接口 ====================

  /**
   * 根据父级ID查询子级行政区划列表
   */
  static queryByParentId = async(parentId: string, tenantId?: string): Promise<ChinaAdministrativeDivisionVO[]> => {
    return request.post(`${this.BASE_URL}/queryByParentId`, { parentId, tenantId })
  }

  /**
   * 根据层级查询行政区划列表
   */
  static queryByLevel = async(level: DivisionLevel, countryId?: string, tenantId?: string): Promise<ChinaAdministrativeDivisionVO[]> => {
    return request.post(`${this.BASE_URL}/queryByLevel`, { level, countryId, tenantId })
  }

  /**
   * 根据编码查询行政区划
   */
  static queryByCode = async(code: string, countryId?: string, tenantId?: string): Promise<ChinaAdministrativeDivisionVO> => {
    return request.post(`${this.BASE_URL}/queryByCode`, { code, countryId, tenantId })
  }

  /**
   * 查询启用状态的行政区划列表
   */
  static queryEnabled = async(countryId?: string, tenantId?: string): Promise<ChinaAdministrativeDivisionVO[]> => {
    return request.post(`${this.BASE_URL}/queryEnabled`, { countryId, tenantId })
  }

  /**
   * 查询国家级行政区划列表（level=1或parentId=0）
   */
  static queryCountries = async(tenantId?: string): Promise<ChinaAdministrativeDivisionVO[]> => {
    return request.post(`${this.BASE_URL}/queryCountries`, { tenantId })
  }

  /**
   * 查询国家级行政区划列表（备用方法，通过level或parentId查询）
   */
  static queryCountriesByLevel = async(tenantId?: string): Promise<ChinaAdministrativeDivisionVO[]> => {
    const params = {
      level: 1,
      parentId: '0',
      tenantId,
      pageNum: 1,
      pagesize: 100
    }
    const response = await request.post(`${this.BASE_URL}/queryPage`, params)
    return (response as any).records || response
  }

  /**
   * 查询省级行政区划列表
   */
  static queryProvinces = async(countryId?: string, tenantId?: string): Promise<ChinaAdministrativeDivisionVO[]> => {
    return request.post(`${this.BASE_URL}/queryProvinces`, { countryId, tenantId })
  }

  /**
   * 查询市级行政区划列表
   */
  static queryCities = async(parentId: string, tenantId?: string): Promise<ChinaAdministrativeDivisionVO[]> => {
    const normalizedParentId = normalizeDivisionParentId(parentId)
    if (!normalizedParentId) return []
    return request.post(`${this.BASE_URL}/queryCities`, { parentId: normalizedParentId, tenantId })
  }

  /**
   * 查询区县级行政区划列表
   */
  static queryDistricts = async(parentId: string, tenantId?: string): Promise<ChinaAdministrativeDivisionVO[]> => {
    const normalizedParentId = normalizeDivisionParentId(parentId)
    if (!normalizedParentId) return []
    return request.post(`${this.BASE_URL}/queryDistricts`, { parentId: normalizedParentId, tenantId })
  }

  /**
   * 查询街道级行政区划列表
   */
  static queryStreets = async(parentId: string, tenantId?: string): Promise<ChinaAdministrativeDivisionVO[]> => {
    return request.post(`${this.BASE_URL}/queryStreets`, { parentId, tenantId })
  }

  /**
   * 查询村委会级行政区划列表
   */
  static queryVillages = async(parentId: string, tenantId?: string): Promise<ChinaAdministrativeDivisionVO[]> => {
    return request.post(`${this.BASE_URL}/queryVillages`, { parentId, tenantId })
  }

  // ==================== 平台数据管理接口 ====================

  /**
   * 分页查询平台行政区划数据
   */
  static queryPlatformPage = async(params: ChinaAdministrativeDivisionQueryRequest): Promise<PageResultVO<ChinaAdministrativeDivisionVO>> => {
    return request.post(`${this.BASE_URL}/queryPlatformPage`, params)
  }

  /**
   * 查询平台行政区划列表
   */
  static queryPlatformList = async(params: ChinaAdministrativeDivisionQueryRequest): Promise<ChinaAdministrativeDivisionVO[]> => {
    return request.post(`${this.BASE_URL}/queryPlatformList`, params)
  }

  /**
   * 批量关联平台行政区划到租户
   */
  static batchAssociate = async(platformIds: string[]): Promise<boolean> => {
    return request.post(`${this.BASE_URL}/batchAssociate`, { platformIds })
  }

  /**
   * 批量取消关联
   */
  static batchDisassociate = async(platformIds: string[]): Promise<boolean> => {
    return request.post(`${this.BASE_URL}/batchDisassociate`, { platformIds })
  }

  /**
   * 查询租户关联的平台行政区划列表
   */
  static queryAssociatedPlatformDivisions = async(tenantId: string): Promise<ChinaAdministrativeDivisionVO[]> => {
    return request.post(`${this.BASE_URL}/queryAssociatedPlatformDivisions`, { tenantId })
  }
}

// ==================== 便捷服务 ====================

/**
 * 中国行政区划管理服务
 */
export const ChinaAdministrativeDivisionService = {
  // 核心CRUD
  queryPage: ChinaAdministrativeDivisionApi.queryPage,
  queryList: ChinaAdministrativeDivisionApi.queryList,
  search: ChinaAdministrativeDivisionApi.search,
  searchProvinces: ChinaAdministrativeDivisionApi.searchProvinces,
  searchCities: ChinaAdministrativeDivisionApi.searchCities,
  getById: ChinaAdministrativeDivisionApi.getById,
  save: ChinaAdministrativeDivisionApi.save,
  delete: ChinaAdministrativeDivisionApi.delete,

  // 批量操作
  batchDelete: ChinaAdministrativeDivisionApi.batchDelete,
  updateStatus: ChinaAdministrativeDivisionApi.updateStatus,
  batchUpdateStatus: ChinaAdministrativeDivisionApi.batchUpdateStatus,

  // 专项查询
  queryByParentId: ChinaAdministrativeDivisionApi.queryByParentId,
  queryByLevel: ChinaAdministrativeDivisionApi.queryByLevel,
  queryByCode: ChinaAdministrativeDivisionApi.queryByCode,
  queryEnabled: ChinaAdministrativeDivisionApi.queryEnabled,
  queryCountries: ChinaAdministrativeDivisionApi.queryCountries,
  queryProvinces: ChinaAdministrativeDivisionApi.queryProvinces,
  queryCities: ChinaAdministrativeDivisionApi.queryCities,
  queryDistricts: ChinaAdministrativeDivisionApi.queryDistricts,
  queryStreets: ChinaAdministrativeDivisionApi.queryStreets,
  queryVillages: ChinaAdministrativeDivisionApi.queryVillages,

  // 平台数据管理
  queryPlatformPage: ChinaAdministrativeDivisionApi.queryPlatformPage,
  queryPlatformList: ChinaAdministrativeDivisionApi.queryPlatformList,
  batchAssociate: ChinaAdministrativeDivisionApi.batchAssociate,
  batchDisassociate: ChinaAdministrativeDivisionApi.batchDisassociate
}

// ==================== 类型别名 ====================

export type ChinaAdministrativeDivisionModel = ChinaAdministrativeDivisionVO

export default ChinaAdministrativeDivisionApi
